/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subject, merge } from 'rxjs';
import { mapTo } from 'rxjs/operators';
import { NvHigThreeQuarterSheetData } from '../components/nv-generic-dialog/nv-generic-dialog.types';
import { NvHigBottomSheetComponent } from '../components/nv-hig-bottom-sheet/nv-hig-bottom-sheet.component';
import { NvHigThreeQuarterSheetComponent } from '../components/nv-hig-three-quarter-sheet/nv-hig-three-quarter-sheet.component';
import { NvHigConfig, NvHigOffDeviceLinkConfig, NV_HIG_CONFIG } from '../hig.config';
import { FocusManager } from './focus-manager';

@Injectable({
    providedIn: 'root'
})
export class OffDeviceLinkService {
    private config: NvHigOffDeviceLinkConfig;
    private tvMode: boolean;
    private isModalSheetOpenSubject: Subject<boolean>;
    get isModalSheetOpened() {
        return this._isModalSheetOpened;
    }
    private _isModalSheetOpened = false;

    private _enabled = false;
    get enabled(): boolean {
        return this._enabled;
    }

    get isModalSheetOpened$(): Observable<boolean> {
        return this.isModalSheetOpenSubject ? this.isModalSheetOpenSubject.asObservable() : EMPTY;
    }

    constructor(
        @Optional() @Inject(NV_HIG_CONFIG) higConfig: NvHigConfig,
        private matBottomSheet: MatBottomSheet,
        private matDialog: MatDialog,
        private focusManager: FocusManager,
        private translateService: TranslateService
    ) {
        this.config = higConfig?.offDeviceLinkConfig;
        this.tvMode = !!higConfig?.tvMode;
    }

    /**
     * Enables off-device link feature.
     *
     * If disabled (default), external links will be opened in new tabs.
     * If enabled, when a user tries to open an external link,
     * a dialog will be presented to instruct the user to open the link on another device.
     *
     * Note that calling this would
     *  - Overwrite `window.open`
     *  - Intercept click events on <a target="_blank">
     */
    enable() {
        window.open = (url?: string, _target?: string, _features?: string, _replace?: boolean) => {
            if (url) {
                this.open(url);
            }
            return null;
        };
        this._enabled = true;
        if (!this.isModalSheetOpenSubject) {
            this.isModalSheetOpenSubject = new Subject<boolean>();
        }
    }

    /**
     * Opens `url` in off-device link dialog if feature is enabled, otherwise open `url` in a new tab
     */
    open(
        url: string,
        options?: {
            shortUrl?: string;
            shortUrlDescription?: string;
            threeQuarterSheetData?: NvHigThreeQuarterSheetData;
            preferBottomSheet?: boolean;
        }
    ): boolean {
        if (!this.config) {
            throw new Error('Off device link configuration is missing!');
        }

        if (this._enabled) {
            let openedByThreeQuarterSheet = false;
            if (!options?.preferBottomSheet && this.matDialog.openDialogs.length > 0) {
                const dialogRef = this.matDialog.openDialogs[this.matDialog.openDialogs.length - 1];
                if (dialogRef.componentInstance instanceof NvHigThreeQuarterSheetComponent) {
                    openedByThreeQuarterSheet = true;
                }
            }

            if (openedByThreeQuarterSheet) {
                const threeQuarterSheet = this.focusManager.restoreFocusAfterClosed(() =>
                    NvHigThreeQuarterSheetComponent.open(this.matDialog, {
                        data: {
                            headerText: { text: options?.shortUrl },
                            bodyText: { text: options?.shortUrlDescription ?? this.config.title },
                            primaryButton: { text: this.config.primaryButtonText },
                            image: { url },
                            threeQuarterSheetData: options?.threeQuarterSheetData
                        },
                        ariaLabel: this.config?.title ? this.translateService.instant(this.config.title) : '',
                        autoFocus: false
                    })
                );

                threeQuarterSheet.componentInstance.primaryButtonClick.subscribe(() => {
                    threeQuarterSheet.close();
                });

                merge(
                    threeQuarterSheet.afterOpened().pipe(mapTo(true)),
                    threeQuarterSheet.afterClosed().pipe(mapTo(false))
                ).subscribe(isOpened => {
                    this._isModalSheetOpened = isOpened;
                    this.isModalSheetOpenSubject.next(isOpened);
                });
            } else {
                // Only show shortURL if available, should not show long URL in content.
                const bottomSheet = this.focusManager.restoreFocusAfterClosed(() =>
                    NvHigBottomSheetComponent.open(
                        this.matBottomSheet,
                        {
                            data: {
                                headerText: { text: this.config.title },
                                bodyText: options?.shortUrl ? { text: options?.shortUrl } : undefined,
                                primaryButton: { text: this.config.primaryButtonText },
                                link: { url, text: options?.shortUrl }
                            },
                            panelClass: 'nv-hig-off-device-link',
                            ariaLabel: this.config?.title ? this.translateService.instant(this.config.title) : ''
                        },
                        this.tvMode
                    )
                );

                bottomSheet.instance.primaryButtonClick.subscribe(() => {
                    bottomSheet.dismiss();
                });

                merge(
                    bottomSheet.afterOpened().pipe(mapTo(true)),
                    bottomSheet.afterDismissed().pipe(mapTo(false))
                ).subscribe(isOpened => {
                    this._isModalSheetOpened = isOpened;
                    this.isModalSheetOpenSubject.next(isOpened);
                });
            }
            return true;
        } else {
            const response = window.open(url, '_blank');
            return response === null ? false : true;
        }
    }
}
